/*
 * Copyright (c) 2021 EGT Digital - All rights reserved.
 *
 * File: index.tsx
 *
 * Written by Yuliyan Milenkov <yuliyan.milenkov@egt-digital.com> on  04.11.22 11:50 am.
 * Last modified: 04.11.22 11:50 am.
 *
 * More information at: https://egt-digital.com/
 */
import React, { useEffect, useState } from 'react';
import { getGameJackpotComponents } from '../api/helpers';
import { SEARCH_SECTION_NAME } from '../constants';
import { useSettings } from '../contexts/SettingsContext';
import eventStore from '../eventStore';
import Game from '../Game';
import ISearchSectionSettings from '../interfaces/ISearchSectionSettings';
import Section from '../Section';
import SearchSectionPlaceholder from './SearchSectionPlaceholder';

const GameComponent = React.memo(Game, (prevProps, nextProps) => {
    return prevProps.gameKey === nextProps.gameKey;
});

const SearchSection = () => {
    const [settings] = useSettings();
    const [search, setSearch] = useState<ISearchSectionSettings>(eventStore.SEARCH.value);

    useEffect(() => {
        const subscription = eventStore.SEARCH.subscribe(value => setSearch(value));

        return () => subscription.unsubscribe();
    }, []);

    const hasNoInputValue = search.inputValue.length === 0;

    const handleClickPlay = ({
        gameKey,
        banners,
        sectionName,
        sectionPosition,
    }: {
        gameKey: string;
        banners: { rows: number; cols: number };
        sectionName: string;
        sectionPosition: number;
    }) => () => {
        eventStore.ON_ANALYTICS_EVENT.next({
            gameKey,
            banners,
            sectionName,
            sectionPosition,
        });
        eventStore.START_GAME.next(gameKey);
    };

    return (
        <>
            {settings.isSearchOpen && (
                <Section
                    group={search.name}
                    count={search.games.length}
                    blocks={search.gridBlocks}
                    label={'Results'}
                    key={search.name}
                    isViewingAll={true}
                    noHeader={hasNoInputValue ? true : false}
                    placeholder={hasNoInputValue && <SearchSectionPlaceholder />}
                    mode={settings.mode}
                >
                    {search.games.length > 0 &&
                        search.games.map(game => {
                            const {
                                jackpotComponent: GameJackpotComponent,
                                jackpotComponentRenderInside,
                            } = getGameJackpotComponents(game.gameKey);

                            return (
                                <GameComponent
                                    gameKey={game.gameKey}
                                    name={game.name}
                                    cols={game.banners.lobby.cols}
                                    // hardcoded for banner to take two rows, revert to game.banners.lobby.rows when not neccessery
                                    rows={2}
                                    key={`${search.name}-${game.gameKey}`}
                                    jackpotName={game.jackpotName || undefined}
                                    jackpotCurrency={settings.currency}
                                    branding={game.branding}
                                    minDenomination={game.minDenomination.get(settings.currency)}
                                    minBet={game.minPossibleBets.get(settings.currency) || 0}
                                    maxBet={game.maxPossibleBets.get(settings.currency) || 0}
                                    onClickPlay={handleClickPlay({
                                        gameKey: game.gameKey,
                                        banners: game.banners.lobby,
                                        sectionName: SEARCH_SECTION_NAME,
                                        sectionPosition: 0,
                                    })}
                                    jackpotComponent={
                                        GameJackpotComponent &&
                                        (() => (
                                            <GameJackpotComponent
                                                gameKey={game.gameKey}
                                                groupName={search.name}
                                                bannerType="vertical"
                                                jackpotName={game.jackpotName || undefined}
                                                denomination={game.minDenomination.get(settings.currency)}
                                                currency={settings.currency}
                                                units={settings.units}
                                                maxBet={game.maxBet?.get(settings.currency)}
                                            />
                                        ))
                                    }
                                    jackpotComponentRenderInside={jackpotComponentRenderInside}
                                />
                            );
                        })}
                </Section>
            )}
        </>
    );
};

export default SearchSection;
